import type { Extensions } from '@tiptap/react'
import * as Y from 'yjs'
import * as yProtocolAwareness from 'y-protocols/awareness'
import { Extension } from '@tiptap/core'
import type { EditorView, DecorationAttrs } from '@tiptap/pm/view'
import {
  redo,
  undo,
  ySyncPlugin,
  yUndoPlugin,
  yUndoPluginKey,
  yCursorPlugin,
} from './y-prosemirror-local'
import { invariant } from '@epic-web/invariant'

export type CollaborationUser = { name: string; color: string; id: string }

export type CollaborationExtensionsOptions = {
  yDoc: Y.Doc
  yFragment: Y.XmlFragment
  yAwareness: yProtocolAwareness.Awareness
  yUndoManager: Y.UndoManager
  instanceId: string
  hasGlobalUndoManager: boolean
}

export const getCollaborationExtensions = ({
  yDoc,
  yAwareness,
  yUndoManager,
  yFragment,
  instanceId,
  hasGlobalUndoManager,
}: CollaborationExtensionsOptions) => {
  return [
    CollaborationExtension.configure({
      yDoc,
      yAwareness,
      yUndoManager,
      yFragment,
      instanceId,
      hasGlobalUndoManager,
    }),
  ] satisfies Extensions
}

/* -------------------------------------------------------------------------------------------------
 * CollaborationExtension (src: https://github.com/ueberdosis/tiptap/blob/main/packages/extension-collaboration/src/collaboration.ts)
 * -----------------------------------------------------------------------------------------------*/

type YSyncOpts = Parameters<typeof ySyncPlugin>[1]

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    collaboration: {
      /**
       * Undo recent changes
       * @example editor.commands.undo()
       */
      undo: () => ReturnType
      /**
       * Reapply reverted changes
       * @example editor.commands.redo()
       */
      redo: () => ReturnType
    }
  }
}

export interface CollaborationOptions {
  yDoc: Y.Doc | null
  yAwareness: yProtocolAwareness.Awareness | null
  yUndoManager: Y.UndoManager | null
  yFragment: Y.XmlFragment | null
  instanceId: string | null
  hasGlobalUndoManager: boolean

  /**
   * Fired when the content from Yjs is initially rendered to Tiptap.
   */
  onFirstRender?: () => void
  render(user: CollaborationUser): HTMLElement
  selectionRender(user: CollaborationUser): DecorationAttrs
  ySyncOptions?: YSyncOpts
}

/**
 * This extension allows you to collaborate with others in real-time.
 * @see https://tiptap.dev/api/extensions/collaboration
 */
const CollaborationExtension = Extension.create<CollaborationOptions>({
  name: 'collaboration',
  priority: 1000,
  addOptions() {
    return {
      yDoc: null,
      yAwareness: null,
      yUndoManager: null,
      yFragment: null,
      instanceId: null,
      hasGlobalUndoManager: false,
      // This renderer func creates a container next to the user selection
      // with a cursor and a span containing the user's name.
      render: (user) => {
        const container = document.createElement('span')
        const caret = document.createElement('span')
        container.classList.add('collaboration-cursor')
        caret.classList.add('collaboration-cursor__caret')
        caret.setAttribute('data-follow-id', user.id)
        caret.setAttribute('style', `border-color: ${user.color}`)

        const label = document.createElement('span')

        label.classList.add('collaboration-cursor__label')
        label.setAttribute('style', `background-color: ${user.color}`)
        label.insertBefore(document.createTextNode(user.name), null)
        container.insertBefore(label, null)
        container.insertBefore(caret, null)

        return container
      },
      selectionRender: (user) => ({
        // Sets opacity to 0.2 for HEX colors (all client colors should be HEX)
        style: `background-color: ${user.color}33`,
        class: 'ProseMirror-yjs-selection',
      }),
    }
  },
  onCreate() {
    if (
      this.editor.extensionManager.extensions.find(
        (extension) => extension.name === 'history'
      )
    ) {
      console.warn(
        '[tiptap warn]: "@tiptap/extension-collaboration" comes with its own history support and is not compatible with "@tiptap/extension-history".'
      )
    }
  },
  addCommands() {
    return {
      undo:
        () =>
        ({ tr, state, dispatch }) => {
          tr.setMeta('preventDispatch', true)

          const undoManager: Y.UndoManager =
            yUndoPluginKey.getState(state).undoManager

          if (undoManager.undoStack.length === 0) {
            return false
          }

          if (!dispatch) {
            return true
          }

          return !!undo(state)
        },
      redo:
        () =>
        ({ tr, state, dispatch }) => {
          tr.setMeta('preventDispatch', true)

          const undoManager: Y.UndoManager =
            yUndoPluginKey.getState(state).undoManager

          if (undoManager.redoStack.length === 0) {
            return false
          }

          if (!dispatch) {
            return true
          }

          return !!redo(state)
        },
    }
  },
  addKeyboardShortcuts() {
    return {
      'Mod-z': () =>
        this.options.hasGlobalUndoManager ? true : this.editor.commands.undo(),
      'Mod-y': () =>
        this.options.hasGlobalUndoManager ? true : this.editor.commands.redo(),
      'Shift-Mod-z': () =>
        this.options.hasGlobalUndoManager ? true : this.editor.commands.redo(),
    }
  },
  addProseMirrorPlugins() {
    const yFragment = this.options.yFragment
    const instanceId = this.options.instanceId
    invariant(yFragment, 'yFragment is required')
    invariant(instanceId, 'instanceId is required')
    // Quick fix until there is an official implementation (thanks to @hamflx).
    // See https://github.com/yjs/y-prosemirror/issues/114 and https://github.com/yjs/y-prosemirror/issues/102
    const yUndoPluginInstance = yUndoPlugin({
      instanceId,
      undoManager: this.options.yUndoManager as any,
    })
    const originalUndoPluginView = yUndoPluginInstance.spec.view

    yUndoPluginInstance.spec.view = (view: EditorView) => {
      const { undoManager } = yUndoPluginKey.getState(view.state)

      if (undoManager.restore) {
        undoManager.restore()
        // eslint-disable-next-line
        undoManager.restore = () => {}
      }

      const viewRet = originalUndoPluginView
        ? originalUndoPluginView(view)
        : undefined

      return {
        destroy: () => {
          const hasUndoManSelf = undoManager.trackedOrigins.has(undoManager)
          // eslint-disable-next-line
          const observers = undoManager._observers

          undoManager.restore = () => {
            if (hasUndoManSelf) {
              undoManager.trackedOrigins.add(undoManager)
            }

            undoManager.doc.on(
              'afterTransaction',
              undoManager.afterTransactionHandler
            )
            // eslint-disable-next-line
            undoManager._observers = observers
          }

          if (viewRet?.destroy) {
            viewRet.destroy()
          }
        },
      }
    }

    const options = this.options.ySyncOptions
    const onFirstRender = this.options.onFirstRender
    const ySyncPluginOptions = {
      ...(options ? { ...options } : {}),
      ...(onFirstRender ? { onFirstRender } : {}),
      instanceId: instanceId as any,
    }

    const ySyncPluginInstance = ySyncPlugin(
      yFragment,
      ySyncPluginOptions as any
    )

    const yCursorPluginInstance = this.options.yAwareness
      ? yCursorPlugin(this.options.yAwareness, {
          cursorBuilder: this.options.render,
          selectionBuilder: this.options.selectionRender,
        })
      : null

    return [
      ySyncPluginInstance,
      yUndoPluginInstance,
      yCursorPluginInstance,
    ].filter(Boolean)
  },
})
